import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";

const Tooltip = (props) => {
  const parentRef = useRef(); // this ref is specific to the entire dropdown component. Used to detect clicks within it.
  const childRef = useRef(); // this ref is specific to the children passed into the drop down component. These children are most likely the first thing the user clicks to open the dropdown.
  const dropdownRef = useRef();
  const [portalPosition, setPortalPosition] = useState(null);
  const outsideControl = props.outsideControl;
  const [isVisible, setIsVisible] = useState(!!props.isVisible);
  const [hideState, setHideState] = useState(true);
  const action = props.type === "hover" ? "mouseover" : "click";

  const handleAction = ({ target }) => {
    if (
      parentRef?.current?.contains(target) ||
      dropdownRef.current?.contains(target)
    ) {
      if (
        childRef.current.contains(target) &&
        childRef.current.state &&
        action === "click"
      ) {
        return setIsVisible(false); // If we clicked the child component, and the dropdown is visible. Hide it.
      }

      if (
        !childRef.current.contains(target) &&
        target.closest("a")?.dataset?.close &&
        action === "click"
      ) {
        return setIsVisible(false);
      }

      return setIsVisible(true);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    if (hideState) return;
    childRef.current.state = isVisible;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  useEffect(() => {
    setHideState(props.hide);
  }, [props.hide]);

  useEffect(() => {
    if (outsideControl) return;
    window.addEventListener(action, handleAction);
    return () => {
      window.removeEventListener(action, handleAction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]); //eslint-disable-line

  //added this to allow for outside control of hiding dropdown
  useEffect(() => {
    if (hideState) return;
    if (!outsideControl) {
      setIsVisible((state) => {
        return state ? props.isVisible : state;
      });
    } else {
      setIsVisible(props.isVisible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isVisible, hideState]);

  useEffect(() => {
    if (action !== "click" || !isVisible || !props.timeout) return;
    const timer = setTimeout(() => {
      setIsVisible(!isVisible);
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, props.timeout, isVisible]);

  useLayoutEffect(() => {
    if (!props.portal || !isVisible || hideState) return;

    const updatePosition = () => {
      const anchor = childRef.current.getBoundingClientRect();
      const menu = dropdownRef.current.getBoundingClientRect();
      const padding = 8;
      const maxHeight = window.innerHeight - padding * 2;
      const height = Math.min(menu.height, maxHeight);
      const below = anchor.bottom + padding;
      const above = anchor.top - padding - height;
      const top =
        below + height <= window.innerHeight - padding
          ? below
          : Math.max(padding, above);
      const left = Math.max(
        padding,
        Math.min(
          anchor.right - menu.width,
          window.innerWidth - menu.width - padding,
        ),
      );
      setPortalPosition((previous) =>
        previous?.top === top && previous?.left === left
          ? previous
          : { top, left },
      );
    };
    const handleScroll = (event) => {
      // Scrolling the menu itself should not dismiss it.
      if (!dropdownRef.current?.contains(event.target)) setIsVisible(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsVisible(false);
    };

    updatePosition();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updatePosition)
        : null;
    // The action row changes height when its buttons wrap at a breakpoint.
    [
      parentRef.current.parentElement,
      childRef.current,
      dropdownRef.current,
    ].forEach((element) => observer?.observe(element));
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.portal, isVisible, hideState]);

  if (hideState) return <>{props.children}</>;

  const dropdown = isVisible && (
    <ArrowDropdown
      ref={dropdownRef}
      style={
        props.portal
          ? {
              position: "fixed",
              zIndex: 10000,
              top: portalPosition?.top ?? 0,
              left: portalPosition?.left ?? 0,
              visibility: portalPosition ? "visible" : "hidden",
              maxWidth: "calc(100vw - 16px)",
              maxHeight: "calc(100vh - 16px)",
              overflow: "auto",
            }
          : { zIndex: 10000 }
      }
      variant={props.theme}
      position={props.portal ? undefined : props.position}
      align={props.align}
      wrapText={props.wrapText}
      hoverAnimation={props.hoverAnimation}
      onClick={(e) => e.preventDefault()}
      padding={props.padding}
    >
      {props.tooltipContent}
    </ArrowDropdown>
  );

  return (
    <TooltipContainer
      ref={parentRef}
      className={props.className}
      width={props.width ? props.width : ""}
    >
      <Box
        ref={(ref) => {
          childRef.current = ref;
        }}
      >
        {props.children}
      </Box>

      {props.portal ? createPortal(dropdown, document.body) : dropdown}
    </TooltipContainer>
  );
};

const TooltipContainer = styled(Box)`
  position: relative;
`;

const ArrowDropdown = styled(Flex)`
  position: absolute;
  z-index: 10;
  white-space: ${(props) => (props.wrapText ? false : "nowrap")};
  ${(props) => props.position}
  ${(props) =>
    props.hoverAnimation &&
    `
    animation: tooltipSlideUp 300ms cubic-bezier(0.4, 0, 0.2, 1);
    
    @keyframes tooltipSlideUp {
      0% {
        opacity: 0;
        transform: translateX(-50%) translateY(8px) scale(0.96);
      }
      100% {
        opacity: 1;
        transform: translateX(-50%) translateY(0) scale(1);
      }
    }
  `}
`;

Tooltip.defaultProps = {
  theme: "menuDropdown",
  align: "right",
  type: "hover",
  wrapText: false,
  timeout: false,
  hoverAnimation: false,
};

export { Tooltip };
